import type { ReactNode } from "react";
import { useId } from "react";

import type { LinkProps } from "@core/components/Link";
import { Link } from "@core/components/Link";
import type { SafeInputHtmlAttributes } from "@core/types/SafeHtmlAttributes";
import classnames from "classnames";

import { useRadioContainerGroupContext } from "../RadioContainerGroupContext";

import styles from "./RadioContainer.module.scss";

// Distributes Omit over the LinkProps union so the href-anchor and button variants
// (LinkAnchorProps | LinkButtonProps) stay mutually exclusive, matching Link's own API.
type DistributiveOmit<T, K extends keyof T> = T extends unknown ? Omit<T, K> : never;

export type RadioContainerActionProps = DistributiveOmit<LinkProps, "children" | "size"> & {
  label: ReactNode;
};

type RadioContainerActionPrivateProps = {
  action: RadioContainerActionProps;
};

export type RadioContainerProps = {
  label: ReactNode;
  value: string;
  name?: string;
  description?: ReactNode;
  icon?: ReactNode;
  // TODO: Keep ReactNode until Tooltip is migrated to core-components; then replace with TooltipProps.
  tooltip?: ReactNode;
  action1?: RadioContainerActionProps;
  action2?: RadioContainerActionProps;
  disabled?: boolean;
  isError?: boolean;
  children?: ReactNode;
} & Omit<SafeInputHtmlAttributes, "type" | "value" | "children" | "className">;

const RadioContainerAction = ({ action }: RadioContainerActionPrivateProps) => {
  const { label, ...linkProps } = action;

  return (
    <Link size="sm" {...linkProps}>
      {label}
    </Link>
  );
};

export const RadioContainer = ({
  label,
  value,
  name,
  description,
  icon,
  tooltip,
  action1,
  action2,
  disabled = false,
  isError = false,
  children,
  ...rest
}: RadioContainerProps) => {
  const inputId = useId();
  const labelId = `${inputId}-label`;
  const descriptionId = description ? `${inputId}-description` : undefined;
  const describedBy = [rest["aria-describedby"], descriptionId].filter(Boolean).join(" ") || undefined;
  const { isError: groupIsError, groupName } = useRadioContainerGroupContext();
  const resolvedIsError = isError || groupIsError;
  const resolvedName = groupName ?? name;

  return (
    <div
      className={classnames(styles.container, {
        [styles.invalid]: resolvedIsError,
      })}
    >
      <label className={styles.header}>
        <div className={styles.content}>
          <input
            {...rest}
            aria-labelledby={rest["aria-labelledby"] ?? (rest["aria-label"] ? undefined : labelId)}
            aria-describedby={describedBy}
            type="radio"
            value={value}
            name={resolvedName}
            disabled={disabled}
            className={classnames(styles.control, { [styles.controlInvalid]: resolvedIsError })}
          />
          <div className={styles.textContent}>
            <div className={styles.valueLine}>
              <span id={labelId} className={styles.valueLabel}>
                {label}
              </span>
              {tooltip && <span className={styles.tooltip}>{tooltip}</span>}
            </div>
            {description && (
              <span id={descriptionId} className={styles.description}>
                {description}
              </span>
            )}
          </div>
        </div>
        {icon !== undefined && <div className={styles.placeholder}>{icon}</div>}
      </label>
      {(action1 || action2) && (
        <div className={styles.actions}>
          {action1 && <RadioContainerAction action={action1} />}
          {action2 && <RadioContainerAction action={action2} />}
        </div>
      )}
      {children !== undefined && <div className={styles.body}>{children}</div>}
    </div>
  );
};
